/**
 * @file ContactFloatingButtonView.jsx
 * @author Daniel Aguirre
 * @description Contact Floating Button View Page
 */

// Dependencies
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VideoCameraFilled,
  MessageFilled,
  CloseOutlined
} from '@ant-design/icons';
import {
  MainButton,
  ChildButton,
  FloatingMenu,
  Directions
} from 'react-floating-button-menu';
import { message } from 'antd';

// Hooks
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Helpers
import { getStorageItem } from 'commons/helpers/storage-helper';

import styles from './styles.module.css';

function ContactFloatingButtonView({ appointmentsZoomActions }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeVideocall, setActiveVideoCall] = React.useState(null);
  const [isMessageActive, setIsMessageActive] = React.useState(false);
  const history = useHistory();
  const { t: translate } = useTranslation();

  const { getZoomVideoCallById } = appointmentsZoomActions;

  useEffect(async () => {
    const activeVideoCallStorage = await getStorageItem('activeVideocall');
    if (activeVideoCallStorage !== null && isOpen && !isMessageActive) {
      setIsMessageActive(true);
      const content = activeVideoCallStorage
        ? translate('commons.activeVideoCalls')
        : translate('commons.noActiveVideoCalls');
      message.info({
        content,
        onClose: () => setIsMessageActive(false)
      });
    }
    setActiveVideoCall(activeVideoCallStorage);
  }, [isOpen]);

  const onGetVideoCallInfo = (_, resp) => {
    const meetingId = resp?.data?.id_zoom_call;
    if (meetingId) {
      history.push('/main/videocalls-frame', { meetingId });
      setIsOpen(!isOpen);
    }
  };

  const onOpenVideoCall = () => {
    const data = { quoteId: activeVideocall };
    getZoomVideoCallById(data, onGetVideoCallInfo);
  };

  return (
    <>
      {isOpen && (
        <div
          onClick={() => {
            setIsOpen(!isOpen);
            if (isOpen) message.destroy();
          }}
          className="absolute w-full h-full z-10 bg-blackTransparent"
        />
      )}
      <FloatingMenu
        className={styles.floatButton}
        slideSpeed={500}
        isOpen={isOpen}
        spacing={8}
        direction={Directions.Up}>
        <MainButton
          isOpen={isOpen}
          iconResting={<MessageFilled className={styles.iconMsg} />}
          iconActive={<CloseOutlined className={styles.iconBlack} />}
          background={
            isOpen
              ? 'var(--color-highLight)'
              : 'var(--color-baxter-teal-medium)'
          }
          onClick={() => setIsOpen(!isOpen)}
          size={70}
        />
        <ChildButton
          onClick={() => {
            history.push('/main/nephro-plus-chat');
            setIsOpen(!isOpen);
          }}
          icon={<MessageFilled className={styles.iconMsg2} />}
          background="var(--color-baxter-teal-medium)"
          size={50}
        />
        <ChildButton
          onClick={() => {
            if (activeVideocall) {
              onOpenVideoCall();
            }
          }}
          icon={
            <VideoCameraFilled
              className={
                activeVideocall ? styles.iconWhite : styles.iconDisabled
              }
            />
          }
          background={
            activeVideocall
              ? 'var(--color-baxter-teal-medium)'
              : 'var(--color-highLight)'
          }
          size={50}
        />
      </FloatingMenu>
    </>
  );
}

/**
 * Component definition of proptypes.
 */
ContactFloatingButtonView.propTypes = {
  appointmentsZoomActions: PropTypes.shape({
    getZoomVideoCallById: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default ContactFloatingButtonView;
